import { useEffect, useRef, useState } from "react";
import { Button, Control } from "./components";
import { JsonImportButton } from "@proof/authoring/json-import";
import {
  draftDefaults,
  readSetupDraft,
  emptySecrets,
  setupDraftKey,
  setupCandidate,
  validateSetupStep,
  draftFromSetupFile,
  type SetupDraft,
  type SetupSecrets,
  type SecretKey,
} from "./setup-model";
import {
  instanceRequest,
  instanceMessage,
  InstanceRequestError,
} from "./instance-api";

const steps = [
  "Deployment",
  "Ownership",
  "PostgreSQL",
  "Object storage",
  "Runtime providers",
  "Review",
];
export function InstanceSetup({
  available,
  platform,
  onComplete,
}: {
  available: boolean;
  platform: string;
  onComplete: () => void;
}) {
  const [draft, setDraft] = useState(() => readSetupDraft(sessionStorage)),
    // What resumes in this tab: only values typed here. Settings loaded from a
    // private setup file are never written to browser storage.
    [typed, setTyped] = useState(() => readSetupDraft(sessionStorage)),
    [secrets, setSecrets] = useState(emptySecrets),
    [step, setStep] = useState(0),
    [visited, setVisited] = useState(0),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [validated, setValidated] = useState(false);
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    try {
      sessionStorage.setItem(setupDraftKey, JSON.stringify(typed));
    } catch {}
  }, [typed]);
  useEffect(() => {
    setValidated(false);
  }, [draft]);
  useEffect(() => {
    setValidated(false);
  }, [secrets]);
  useEffect(() => {
    heading.current?.focus();
  }, [step]);
  const update = (key: keyof SetupDraft, value: string) => {
    setDraft((old) => ({ ...old, [key]: value }));
    setTyped((old) => ({ ...old, [key]: value }));
  };
  const changeSecret = (
    key: SecretKey,
    value: Partial<SetupSecrets[SecretKey]>,
  ) => setSecrets((old) => ({ ...old, [key]: { ...old[key], ...value } }));
  const field = (
    key: keyof SetupDraft,
    label: string,
    note?: string,
    placeholder?: string,
    type = "text",
  ) => (
    <Control label={label} note={note}>
      <input
        value={draft[key]}
        onChange={(event) => update(key, event.target.value)}
        placeholder={placeholder}
        type={type}
        maxLength={16384}
      />
    </Control>
  );
  const toggle = (key: keyof SetupDraft, label: string) => (
    <label className="instance-check">
      <input
        type="checkbox"
        checked={draft[key] === "true"}
        onChange={(event) => update(key, String(event.target.checked))}
      />
      {label}
    </label>
  );
  const secret = (key: SecretKey, label: string, mounted = true) => (
    <div className="instance-secret">
      {mounted && (
        <Control label={`${label} source`}>
          <select
            value={secrets[key].mode}
            onChange={(event) =>
              changeSecret(key, {
                mode: event.target.value as "value" | "file",
                value: "",
              })
            }
          >
            <option value="value">Enter value</option>
            <option value="file">Mounted secret file</option>
          </select>
        </Control>
      )}
      <Control
        label={label}
        note={
          mounted && secrets[key].mode === "file"
            ? "Absolute file path on the API host."
            : undefined
        }
      >
        {key === "redisCa" && secrets[key].mode === "value" ? (
          <textarea
            value={secrets[key].value}
            onChange={(event) =>
              changeSecret(key, { value: event.target.value })
            }
            autoComplete="off"
            rows={4}
            maxLength={65536}
          />
        ) : (
          <input
            type={secrets[key].mode === "file" ? "text" : "password"}
            value={secrets[key].value}
            onChange={(event) =>
              changeSecret(key, { value: event.target.value })
            }
            autoComplete={
              key === "password" || key === "confirm" ? "new-password" : "off"
            }
            maxLength={65536}
          />
        )}
      </Control>
      {(key === "login" || key === "rate") && secrets[key].mode === "value" && (
        <Button
          onClick={() => {
            const bytes = crypto.getRandomValues(new Uint8Array(32));
            changeSecret(key, {
              value: Array.from(bytes, (byte) =>
                byte.toString(16).padStart(2, "0"),
              ).join(""),
            });
          }}
        >
          Generate {key === "login" ? "login" : "request"} key
        </Button>
      )}
    </div>
  );
  const s3 = (which: "object" | "artifact") => (
    <div className="instance-subgroup">
      <h3>{which === "object" ? "Source" : "Artifact"} S3 connection</h3>
      <div className="instance-grid">
        {field(`${which}Endpoint`, "Endpoint", "Leave blank for AWS S3.")}
        {field(`${which}Region`, "Region")}
        {field(`${which}Bucket`, "Bucket")}
        {field(`${which}Prefix`, "Prefix")}
        {toggle(`${which}PathStyle`, "Use path-style addressing")}
        {toggle(`${which}Http`, "Allow HTTP for local development")}
        {secret(`${which}Access`, "Access key")}
        {secret(`${which}Secret`, "Secret key")}
        {secret(`${which}Session`, "Session token (optional)")}
      </div>
    </div>
  );
  const checkAll = () => {
    for (let index = 0; index < 5; index++) {
      const message = validateSetupStep(index, draft, secrets);
      if (message) {
        setError(message);
        setStep(index);
        return false;
      }
    }
    return true;
  };
  const submit = async (validate: boolean) => {
    if (!checkAll()) return;
    setBusy(true);
    setError("");
    try {
      await instanceRequest(
        `/api/instance/setup/${validate ? "validate" : "complete"}`,
        setupCandidate(draft, secrets),
        secrets.bootstrap.value,
        AbortSignal.timeout(180000),
      );
      if (validate) setValidated(true);
      else {
        setSecrets(emptySecrets());
        try {
          sessionStorage.removeItem(setupDraftKey);
        } catch {}
        onComplete();
      }
    } catch (cause) {
      if (
        cause instanceof InstanceRequestError &&
        cause.code === "SETUP_CLOSED"
      )
        onComplete();
      else setError(instanceMessage(cause));
    } finally {
      setBusy(false);
    }
  };
  const download = () => {
    if (!checkAll()) return;
    try {
      const url = URL.createObjectURL(
          new Blob([JSON.stringify(setupCandidate(draft, secrets), null, 2)], {
            type: "application/json",
          }),
        ),
        anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "proof-instance-setup.json";
      anchor.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      setError("The setup file could not be created.");
    }
  };
  return (
    <main className="instance-shell">
      <header className="instance-brand">
        Proof Studio <span>Instance setup</span>
      </header>
      <div className="instance-layout">
        <nav aria-label="Setup steps">
          <ol>
            {steps.map((label, index) => (
              <li key={label}>
                <button
                  disabled={busy || index > visited}
                  aria-current={step === index ? "step" : undefined}
                  onClick={() => {
                    setStep(index);
                    setError("");
                  }}
                >
                  <span>{index + 1}</span>
                  {label}
                </button>
              </li>
            ))}
          </ol>
        </nav>
        <section className="instance-panel">
          <p className="instance-eyebrow">Step {step + 1} of 6</p>
          <h1 ref={heading} tabIndex={-1}>
            {steps[step]}
          </h1>
          <div className="instance-intro-row">
            <p className="instance-muted">
              Non-secret settings resume in this tab. Passwords, tokens, keys
              and mounted-secret paths are cleared on reload.
            </p>
            <JsonImportButton
              label="Load a saved setup file"
              title="Load a saved setup file"
              disabled={busy}
              description={
                <p>
                  Choose a file saved with “Download private setup file”. Its
                  settings fill in this wizard so you can review each step.
                  Nothing from the file is kept in the browser: after a reload,
                  load it again. Enter the owner password on the Owner step.
                </p>
              }
              maxBytes={65_536}
              inspect={(value) => {
                const result = draftFromSetupFile(value);
                return {
                  title: result.draft.name || "Setup file",
                  summary: [
                    { label: "Profile", value: result.draft.profile },
                    { label: "Web address", value: result.draft.publicOrigin || "Not set" },
                    { label: "Owner", value: result.draft.username || "Not set" },
                  ],
                  before: draft,
                  after: result.draft,
                  warnings: [
                    "Secrets in the file are filled in too. Treat this file like a password.",
                  ],
                  confirmLabel: "Fill in the wizard",
                };
              }}
              onConfirm={(value) => {
                const result = draftFromSetupFile(value);
                setDraft(result.draft);
                setSecrets({ ...result.secrets, bootstrap: secrets.bootstrap });
                setVisited(steps.length - 1);
                setValidated(false);
                setError("");
                return `Loaded ${result.restored.length} sections. Review each step, enter the owner password, then validate.`;
              }}
            />
          </div>
          {!available && (
            <p role="alert">
              Browser setup is unavailable until the API administrator
              configures a bootstrap token. Offline CLI setup remains available.
            </p>
          )}
          <fieldset disabled={busy} className="instance-fields">
            {step === 0 && (
              <div className="instance-grid">
                <Control label="Deployment profile">
                  <select
                    value={draft.profile}
                    onChange={(event) => update("profile", event.target.value)}
                  >
                    <option value="development">
                      Development / evaluation
                    </option>
                    <option value="production">Production</option>
                  </select>
                </Control>
                {field(
                  "publicOrigin",
                  "Public origin",
                  "The exact browser origin used for setup and operator actions.",
                )}
                {field(
                  "dataDirectory",
                  "API data folder",
                  "Absolute folder on the API host.",
                  platform === "windows"
                    ? "C:\\ProofStudio\\data"
                    : "/var/lib/proofstudio/data",
                )}
                <Control label="Allowed embed origins">
                  <textarea
                    value={draft.embedOrigins}
                    onChange={(event) =>
                      update("embedOrigins", event.target.value)
                    }
                    rows={4}
                    placeholder="One origin per line"
                    maxLength={16384}
                  />
                </Control>
                <p className="instance-note">
                  Production setup requires PostgreSQL TLS, private S3, host
                  identity and Redis. Provider checks validate connections;
                  actual production deployment still needs qualification.
                </p>
              </div>
            )}
            {step === 1 && (
              <div className="instance-grid">
                {field("name", "Instance name")}
                {field("username", "Owner username")}
                {secret("password", "Owner password", false)}
                {secret("confirm", "Confirm owner password", false)}
                {secret("bootstrap", "Bootstrap token", false)}
                <Control label="Trusted proxy IP addresses">
                  <textarea
                    value={draft.proxies}
                    onChange={(event) => update("proxies", event.target.value)}
                    rows={3}
                    placeholder="Exact direct proxy IPs, one per line"
                    maxLength={2048}
                  />
                </Control>
                {field(
                  "hstsDays",
                  "HSTS duration (days)",
                  undefined,
                  undefined,
                  "number",
                )}
              </div>
            )}
            {step === 2 && (
              <div className="instance-grid">
                {secret("database", "PostgreSQL connection URL")}
                {toggle(
                  "postgresPlaintext",
                  "Allow plaintext PostgreSQL for development",
                )}
                {field(
                  "caFile",
                  "Mounted CA certificate file (optional)",
                  "Leave blank to use the platform trust store.",
                )}
                <p className="instance-note">
                  Use a PostgreSQL URL with username, password and database.
                  Fresh setup applies the checked-in migrations; pre-migrated
                  deployments may use a restricted runtime role.
                </p>
              </div>
            )}
            {step === 3 && (
              <>
                <div className="instance-grid">
                  <Control label="Source image and font storage">
                    <select
                      value={draft.objects}
                      onChange={(event) =>
                        update("objects", event.target.value)
                      }
                    >
                      <option value="postgres">PostgreSQL</option>
                      <option value="filesystem">
                        Filesystem (development)
                      </option>
                      <option value="s3">S3 compatible</option>
                    </select>
                  </Control>
                  <Control label="Generated artifact storage">
                    <select
                      value={draft.artifacts}
                      onChange={(event) =>
                        update("artifacts", event.target.value)
                      }
                    >
                      <option value="postgres">PostgreSQL</option>
                      <option value="s3">S3 compatible</option>
                    </select>
                  </Control>
                </div>
                {draft.objects === "s3" && s3("object")}
                {draft.artifacts === "s3" && s3("artifact")}
              </>
            )}
            {step === 4 && (
              <>
                <div className="instance-grid">
                  <Control label="Queue provider">
                    <select
                      value={draft.queue}
                      onChange={(event) => update("queue", event.target.value)}
                    >
                      <option value="embedded">
                        Embedded durable coordinator
                      </option>
                      <option value="redis">
                        Redis dispatch with durable PostgreSQL outbox
                      </option>
                    </select>
                  </Control>
                  {draft.queue === "redis" && (
                    <>
                      {secret("redis", "Redis connection URL")}
                      <p className="instance-note">
                        Use rediss:// for TLS. Development alone allows
                        redis://. Credentials stay in memory or mounted secrets.
                      </p>
                      {toggle(
                        "redisCaEnabled",
                        "Use a private Redis CA certificate",
                      )}
                      {draft.redisCaEnabled === "true" &&
                        secret("redisCa", "Redis CA certificate")}
                    </>
                  )}
                  <Control label="Tenant identity">
                    <select
                      value={draft.identity}
                      onChange={(event) =>
                        update("identity", event.target.value)
                      }
                    >
                      <option value="development">Development identity</option>
                      <option value="http">HTTP host adapter</option>
                    </select>
                  </Control>
                  <p className="instance-note">
                    Development identity grants the fixed demo tenant
                    administrator access without sign-in. Platform operators
                    remain separate from tenant users.
                  </p>
                  {draft.identity === "http" && (
                    <>
                      {field("identityEndpoint", "Identity endpoint")}
                      {secret("identity", "Identity service token")}
                      {toggle(
                        "identityHttp",
                        "Allow HTTP identity for local development",
                      )}
                    </>
                  )}
                  {toggle(
                    "contextEnabled",
                    "Configure optional HTTP user context",
                  )}
                  {draft.contextEnabled === "true" && (
                    <>
                      {field("contextEndpoint", "Context endpoint")}
                      {secret("context", "Context service token")}
                      {toggle(
                        "contextHttp",
                        "Allow HTTP context for local development",
                      )}
                    </>
                  )}
                  {toggle(
                    "deliveryEnabled",
                    "Configure optional order delivery",
                  )}
                  {draft.deliveryEnabled === "true" && (
                    <>
                      <p className="instance-note">
                        Requires HTTP tenant identity. Setup checks host status
                        without submitting a cart or order.
                      </p>
                      {field(
                        "deliveryId",
                        "Ordering host ID",
                        "Keep this identity stable for the host transaction ledger.",
                      )}
                      {field("deliveryLabel", "Ordering display name")}
                      {field("deliveryEndpoint", "Ordering endpoint")}
                      {secret("delivery", "Ordering service token")}
                      {field(
                        "deliveryTimeout",
                        "Ordering timeout (milliseconds)",
                        "100–5,000 milliseconds.",
                        undefined,
                        "number",
                      )}
                      {toggle(
                        "deliveryHttp",
                        "Allow HTTP ordering for local development",
                      )}
                    </>
                  )}
                  {secret("login", "Operator login key")}
                  {secret("rate", "Request limit key")}
                  {field(
                    "sessionMinutes",
                    "Session lifetime (minutes)",
                    undefined,
                    undefined,
                    "number",
                  )}
                  {field(
                    "concurrency",
                    "Render concurrency",
                    undefined,
                    undefined,
                    "number",
                  )}
                  {field(
                    "leaseSeconds",
                    "Worker lease (seconds)",
                    undefined,
                    undefined,
                    "number",
                  )}
                </div>
              </>
            )}
            {step === 5 && (
              <>
                <dl className="instance-summary">
                  <dt>Instance</dt>
                  <dd>{draft.name}</dd>
                  <dt>Owner</dt>
                  <dd>{draft.username}</dd>
                  <dt>Public origin</dt>
                  <dd>{draft.publicOrigin}</dd>
                  <dt>Source / artifact storage</dt>
                  <dd>
                    {draft.objects} / {draft.artifacts}
                  </dd>
                  <dt>Tenant identity</dt>
                  <dd>{draft.identity}</dd>
                  <dt>Queue</dt>
                  <dd>
                    {draft.queue === "redis"
                      ? "Redis dispatch / PostgreSQL outbox"
                      : "Embedded durable coordinator"}
                  </dd>
                  <dt>Order delivery</dt>
                  <dd>
                    {draft.deliveryEnabled === "true"
                      ? `${draft.deliveryLabel} (${draft.deliveryId})`
                      : "Disabled"}
                  </dd>
                  <dt>Secrets</dt>
                  <dd>Supplied separately; never included in public status.</dd>
                </dl>
                <p className="instance-note">
                  Keep a private copy for an identical retry if setup is
                  interrupted. The downloaded file contains credentials and
                  excludes the bootstrap token.
                </p>
                <Button onClick={download}>Download private setup file</Button>
                {validated && <p role="status">Provider checks passed.</p>}
              </>
            )}
          </fieldset>
          {error && (
            <p role="alert" className="instance-error">
              {error}
            </p>
          )}
          <footer className="instance-actions">
            {step > 0 && (
              <Button
                disabled={busy}
                onClick={() => {
                  setStep(step - 1);
                  setError("");
                }}
              >
                Back
              </Button>
            )}
            {step < 5 ? (
              <Button
                kind="primary"
                disabled={busy}
                onClick={() => {
                  const message = validateSetupStep(step, draft, secrets);
                  if (message) {
                    setError(message);
                    return;
                  }
                  setError("");
                  setVisited(Math.max(visited, step + 1));
                  setStep(step + 1);
                }}
              >
                Next
              </Button>
            ) : (
              <>
                <Button
                  disabled={busy || !available}
                  onClick={() => void submit(true)}
                >
                  Validate connections
                </Button>
                <Button
                  kind="primary"
                  disabled={busy || !available}
                  onClick={() => void submit(false)}
                >
                  {busy ? "Working…" : "Create instance"}
                </Button>
              </>
            )}
          </footer>
        </section>
      </div>
    </main>
  );
}
